(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';
  const MOBILE_TRIGGER_TOLERANCE = 40;
  const MOBILE_TRIGGER_RATIO = 0.6;
  const DRAG_THRESHOLD = 24;

  const pauseCardVideo = (card) => {
    const video = card.querySelector('video');
    if (video) video.pause();
  };

  const playCardVideo = (card) => {
    const video = card.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const setActiveCard = (root, cards, activeCard) => {
    cards.forEach((card) => {
      const isActive = card === activeCard;
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-expanded', String(isActive));

      if (isActive && card.dataset.nanoPeakMedia === 'video') {
        playCardVideo(card);
      } else {
        pauseCardVideo(card);
      }
    });
  };

  const getTriggeredImageCard = (cards) => {
    const triggerY = window.innerHeight * MOBILE_TRIGGER_RATIO;
    let closestCard = null;
    let closestDistance = Infinity;

    cards.forEach((card) => {
      if (card.dataset.nanoPeakMedia !== 'image') return;

      const rect = card.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      const cardCenter = rect.top + rect.height / 2;
      const distance = Math.abs(cardCenter - triggerY);
      if (distance > MOBILE_TRIGGER_TOLERANCE) return;

      if (distance < closestDistance) {
        closestDistance = distance;
        closestCard = card;
      }
    });

    return closestCard;
  };

  const initPeak = (root) => {
    if (root.dataset.nanoPeakReady === 'true') return;
    root.dataset.nanoPeakReady = 'true';

    const cards = Array.from(root.querySelectorAll('[data-nano-peak-card]'));
    if (cards.length === 0) return;

    const cardsEl = root.querySelector('.nano-peak__cards');
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    let ticking = false;
    let isTransitioning = false;
    let isDragging = false;
    let hasDragged = false;
    let dragStartX = 0;

    const activateTriggeredMobileCard = () => {
      if (!mediaQuery.matches) return;

      const triggeredCard = getTriggeredImageCard(cards);
      if (triggeredCard) setActiveCard(root, cards, triggeredCard);
    };

    const requestMobileActivation = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        ticking = false;
        activateTriggeredMobileCard();
      });
    };

    const goTo = (index) => {
      if (isTransitioning) return;

      const clamped = Math.max(0, Math.min(cards.length - 1, index));
      isTransitioning = true;
      setActiveCard(root, cards, cards[clamped]);

      window.setTimeout(() => {
        isTransitioning = false;
      }, 240);
    };

    cards.forEach((card, index) => {
      card.addEventListener('click', () => {
        goTo(index);
      });

      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        goTo(index);
      });
    });

    cardsEl?.addEventListener('pointerdown', (event) => {
      if (cards.length < 2 || isTransitioning || (event.pointerType === 'mouse' && event.button !== 0)) return;

      isDragging = true;
      hasDragged = false;
      dragStartX = event.clientX;
    });

    cardsEl?.addEventListener('pointermove', (event) => {
      if (!isDragging) return;

      const delta = event.clientX - dragStartX;
      if (!hasDragged && Math.abs(delta) < 5) return;

      if (!hasDragged) hasDragged = true;
    });

    const stopDrag = (event) => {
      if (!isDragging) return;

      const delta = event.clientX - dragStartX;
      isDragging = false;

      if (hasDragged && Math.abs(delta) >= DRAG_THRESHOLD) {
        const currentIndex = cards.findIndex((c) => c.classList.contains('is-active'));
        goTo(currentIndex + (delta < 0 ? 1 : -1));
      }

      cardsEl?.releasePointerCapture?.(event.pointerId);
      hasDragged = false;
    };

    cardsEl?.addEventListener('pointerup', stopDrag);
    cardsEl?.addEventListener('pointercancel', stopDrag);

    window.addEventListener('scroll', requestMobileActivation, { passive: true });
    window.addEventListener('resize', requestMobileActivation);

    const activeCard = cards.find((card) => card.classList.contains('is-active')) || cards[0];
    setActiveCard(root, cards, activeCard);
    requestMobileActivation();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-nano-peak]').forEach(initPeak);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
